/**
 * BacktestResults.tsx
 *
 * Container component for displaying backtest results.
 * Orchestrates the chart, comparison table, and trade list.
 */

import React, { useState } from 'react';
import { BacktestResponse, formatStrategyName, getStrategyColor } from '../../types/backtest';
import BacktestChart from './BacktestChart';
import StrategyComparison from './StrategyComparison';
import TradeList from './TradeList';

interface BacktestResultsProps {
  results: BacktestResponse;
  onNewBacktest?: () => void;
}

const BacktestResults: React.FC<BacktestResultsProps> = ({ results, onNewBacktest }) => {
  // Get list of available strategies and use first as default
  const availableStrategies = Object.keys(results.strategies);
  const [selectedStrategy, setSelectedStrategy] = useState<string>(
    availableStrategies.includes('recommended') ? 'recommended' : availableStrategies[0] || 'recommended'
  );

  const formatPercent = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatCurrency = (value: number): string => {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Get best performing strategy data
  const bestStrategy = results.comparison_summary.best_strategy;
  const bestStrategyData = results.strategies[bestStrategy];

  const tone = (value: number): string => (value >= 0 ? 'is-gain' : 'is-loss');

  return (
    <div>
      {/* Summary Header */}
      <div className="bt-head">
        <div>
          <h2 className="bt-title">
            <span className="n">{results.symbol}</span> Backtest Results
          </h2>
          <p className="bt-sub">
            <span className="n">{results.period.start}</span> to <span className="n">{results.period.end}</span>
          </p>
        </div>
        <div className="bt-head-r">
          <div className="bt-best">
            <span className="bt-best-l">Best Strategy</span>
            <span className="bt-best-v">
              <span className="bt-dot" style={{ background: getStrategyColor(bestStrategy) }} />
              {formatStrategyName(bestStrategy)}
              <span className={`n ${tone(results.comparison_summary.best_return_pct)}`}>
                {formatPercent(results.comparison_summary.best_return_pct)}
              </span>
            </span>
          </div>
          {onNewBacktest && (
            <button type="button" className="bt-btn" onClick={onNewBacktest}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
              </svg>
              New Backtest
            </button>
          )}
        </div>
      </div>

      {/* Alpha vs Benchmarks */}
      <div className="bt-kpis">
        <div className="bt-kpi">
          <div className="bt-kpi-l">vs SPY Alpha</div>
          <div className={`bt-kpi-v n ${tone(results.comparison_summary.vs_spy_alpha)}`}>
            {formatPercent(results.comparison_summary.vs_spy_alpha)}
          </div>
        </div>
        <div className="bt-kpi">
          <div className="bt-kpi-l">vs SPYI Alpha</div>
          <div className={`bt-kpi-v n ${tone(results.comparison_summary.vs_spyi_alpha)}`}>
            {formatPercent(results.comparison_summary.vs_spyi_alpha)}
          </div>
        </div>
        <div className="bt-kpi">
          <div className="bt-kpi-l">Total Trades ({formatStrategyName(bestStrategy)})</div>
          <div className="bt-kpi-v n">
            {bestStrategyData?.total_trades ?? 0}
          </div>
        </div>
        <div className="bt-kpi">
          <div className="bt-kpi-l">Win Rate ({formatStrategyName(bestStrategy)})</div>
          <div className="bt-kpi-v n">
            {bestStrategyData?.win_rate?.toFixed(1) ?? '0.0'}%
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <section className="bt-sec">
        <div className="bt-sec-h">
          <h3 className="bt-h">Performance Comparison</h3>
        </div>
        <BacktestChart results={results} />
      </section>

      {/* Strategy Comparison Table */}
      <section className="bt-sec">
        <div className="bt-sec-h">
          <h3 className="bt-h">Strategy Metrics</h3>
        </div>
        <StrategyComparison results={results} />
      </section>

      {/* Trade List */}
      <section className="bt-sec">
        <div className="bt-sec-h">
          <h3 className="bt-h">Trade History</h3>
          <div className="seg is-wrap" role="group" aria-label="Strategy">
            {Object.keys(results.strategies).map((strategy) => (
              <button
                key={strategy}
                type="button"
                aria-pressed={selectedStrategy === strategy}
                onClick={() => setSelectedStrategy(strategy)}
              >
                <span className="bt-dot" style={{ background: getStrategyColor(strategy) }} />
                {formatStrategyName(strategy)}
              </button>
            ))}
          </div>
        </div>
        <TradeList
          trades={results.strategies[selectedStrategy]?.trades ?? []}
          strategyName={formatStrategyName(selectedStrategy)}
        />
      </section>
    </div>
  );
};

export default BacktestResults;
